import Vue from "vue";
import VueRouter from "vue-router";
import { AUTH_ROUTE_FALLBACK } from "@/constants/auth";
import { findFirstMenuPath, flattenMenuTree, normalizeMenuTree } from "@/utils/menu";

Vue.use(VueRouter);

const Index = () => import("@/views/Index.vue");
const Login = () => import("@/views/Login.vue");

export const routeRegistry = {
  "/index/accessad": () => import("@/components/accessad/Accessad.vue"),
  "/index/adn": () => import("@/components/adn/Adn.vue"),
  "/index/admin_user": () => import("@/components/adminUser/adminUser.vue"),
  "/index/app_detail": () => import("@/components/app/AppInfo.vue"),
  "/index/app_rules": () => import("@/components/appRules/AppRules.vue"),
  "/index/appdata": () => import("@/components/appData/AppData.vue"),
  "/index/applist": () => import("@/components/app/App.vue"),
  "/index/channel": () => import("@/components/channel/Channel.vue"),
  "/index/channel_detail": () => import("@/components/channel/ChannelDetail.vue"),
  "/index/config": () => import("@/components/config/Config.vue"),
  "/index/data": () => import("@/components/data/Data.vue"),
  "/index/defult": () => import("@/components/defult.vue"),
  "/index/menulist": () => import("@/components/menu/MenuList.vue"),
  "/index/new_user": () => import("@/components/newUser/NewUser.vue"),
  "/index/payouts": () => import("@/components/finance/Payouts.vue"),
  "/index/report_full": () => import("@/components/report/ReportFull.vue"),
  "/index/report_hourly": () => import("@/components/report/ReportHourly.vue"),
  "/index/schedulers": () => import("@/components/schedulers/Schedulers.vue"),
  "/index/tasks": () => import("@/components/tasks/Tasks.vue"),
  "/index/user": () => import("@/components/user/User.vue"),
  "/index/bill": () => import("@/components/bill/Bill.vue"),
};

export const hiddenRouteRegistry = {
  "/index/app_detail": {
    component: routeRegistry["/index/app_detail"],
    permissionPath: "/index/applist",
    title: "APP详情",
  },
  "/index/channel_detail": {
    component: routeRegistry["/index/channel_detail"],
    permissionPath: "/index/channel",
    title: "渠道详情",
  },
};

const staticRoutes = [
  {
    path: "/",
    redirect: "/login",
  },
  {
    path: "/login",
    name: "login",
    component: Login,
  },
  {
    path: "/index",
    name: "layout",
    component: Index,
    children: [],
  },
];

export function createRouter() {
  return new VueRouter({
    mode: "history",
    routes: staticRoutes,
  });
}

const router = createRouter();

export function resetRouter() {
  const freshRouter = createRouter();
  router.matcher = freshRouter.matcher;
}

export function buildDynamicRoutes(menuTree = []) {
  const normalizedMenuTree = normalizeMenuTree(menuTree);
  const flatMenus = flattenMenuTree(normalizedMenuTree);

  return flatMenus
    .filter((item) => item.type === "menu" && item.path && routeRegistry[item.path])
    .map((item) => ({
      path: item.path.replace("/index/", ""),
      name: item.path,
      component: routeRegistry[item.path],
      meta: {
        dynamic: true,
        permissionKey: item.permission_key || "",
        title: item.title,
        fullPath: item.path,
      },
    }));
}

export function registerDynamicRoutes(menuTree = []) {
  const dynamicRoutes = buildDynamicRoutes(menuTree);
  const layoutRoute = router.options.routes.find((item) => item.name === "layout");
  const existingPaths = new Set(router.getRoutes().map((item) => item.path));

  if (layoutRoute) {
    layoutRoute.children = dynamicRoutes;
  }

  dynamicRoutes.forEach((route) => {
    if (!existingPaths.has(`/index/${route.path}`)) {
      router.addRoute("layout", route);
    }
  });

  Object.entries(hiddenRouteRegistry).forEach(([fullPath, config]) => {
    const hasParentPermission = dynamicRoutes.some(
      (route) => route.meta.fullPath === config.permissionPath
    );
    const childPath = fullPath.replace("/index/", "");

    if (hasParentPermission && !existingPaths.has(`/index/${childPath}`)) {
      router.addRoute("layout", {
        path: childPath,
        name: fullPath,
        component: config.component,
        meta: {
          dynamic: true,
          hidden: true,
          inheritedFrom: config.permissionPath,
          title: config.title,
          fullPath,
        },
      });
    }
  });

  return dynamicRoutes;
}

export function getRegisteredRoutePaths(menuTree = []) {
  const dynamicRoutes = buildDynamicRoutes(menuTree).map(
    (route) => route.meta.fullPath
  );
  const hiddenRoutes = Object.entries(hiddenRouteRegistry)
    .filter(([, config]) =>
      dynamicRoutes.includes(config.permissionPath)
    )
    .map(([fullPath]) => fullPath);

  return [...dynamicRoutes, ...hiddenRoutes];
}

export function resolveDefaultPath(menuTree = [], defaultPath = "") {
  const normalizedMenuTree = normalizeMenuTree(menuTree);
  const registeredPaths = getRegisteredRoutePaths(normalizedMenuTree);

  if (defaultPath && registeredPaths.includes(defaultPath)) {
    return defaultPath;
  }

  const firstPath = findFirstMenuPath(
    normalizedMenuTree.filter((item) => {
      if (item.type !== "menu") {
        return true;
      }
      return registeredPaths.includes(item.path);
    })
  );

  if (firstPath && registeredPaths.includes(firstPath)) {
    return firstPath;
  }

  if (registeredPaths.includes(AUTH_ROUTE_FALLBACK)) {
    return AUTH_ROUTE_FALLBACK;
  }

  return "/login";
}

export default router;
